/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { faExchange, faPlus } from "@fortawesome/pro-regular-svg-icons";
import Papa from "papaparse";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
    FormGroup, FormText,
    Input,
    Label,
    Modal, ModalBody, ModalFooter, ModalHeader, Table
} from "reactstrap";
import { createTenantDefect, deleteAllTenantDefect, deleteTenantDefect, getTenant, getTenantDefects, importTenantDefect, updateTenantDefect } from "../../../actions/Tenants/actions";
import { formatDateForFilename } from "../Layers/fields/formatters";
import useConfig, { useEDDateFormats } from "../../../actions/Tenants/config/configHook";
import { AvosAction } from "../../../actions/types";
import MetaForm, { metaFormIsValid } from "../../Forms/MetaForm";
import { Button } from "../../Helper/Button";
import { FormContainer } from "../../Helper/FormContainer";
import { Loading } from "../../Helper/Loading";
import FullPageLayout from "../Layout/FullPageLayout";
import TenantPageWrappedInConfigProvider from "./TenantPageWrappedInConfigProvider";

const defaultValues = {
    country: null,
    supplier: null,
    global_gap_number: null,
    label: "",
    value: "",
    image_url: null,
    id: null,
};

const defaultCsvOption = {
    country: null,
    supplier: null,
    fruit_type: null,
    global_gap_number: null,
    label: null,
    value: null,
    image_url: null
};

type ImportData = {
    blob_name: string;
    fruit_type: string;
    defect_id: string;
    position: string;
    severity: string;
    value: string;
    label: string;
    description: string;
    image_url: string;
}

// * Wrap this page in the config of the tenant we are editing
export default function WrappedInConfigProviderTenantEditForm() {
    return (
        <TenantPageWrappedInConfigProvider>
            <TenantEditDefectsForm />
        </TenantPageWrappedInConfigProvider>
    );
}

function TenantEditDefectsForm() {
    const current = useSelector((state: any) => state.tenants.current);
    const options = useSelector((state: any) => state.tenants.defects);
    const isLoading = useSelector((state: any) => state.tenants.isLoading);

    const params = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const config = useConfig();
    const edFormats = useEDDateFormats();

    // Memoize defect positions and severities from config - returns { options, values }
    const defectPositionsData = useMemo(() => config?.get_defect_positions() || { options: [], values: [] }, [config]);
    const defectSeveritiesData = useMemo(() => config?.get_defect_severities() || { options: [], values: [] }, [config]);

    // Extract values array for filtering and validation
    const defectPositions = defectPositionsData.values;
    const defectSeverities = defectSeveritiesData.values;

    // Extract options array for form fields
    const defectPositionOptions = defectPositionsData.options;
    const defectSeverityOptions = defectSeveritiesData.options;

    const [isOpen, setModal] = useState(false);

    const [importModal, setImportModal] = useState(false);
    const [showAllToBeImportedItems, setShowAllToBeImportedItems] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | false>(false);
    const [importData, setImportData] = useState<ImportData[]>([]);

    const [query, setQuery] = useState(defaultValues);
    const [filter, setFilter] = useState({ fruit_type: params.fruit_type, position: [] as string[], severity: [] as string[] });

    const { tenant_name } = current;
    const { fruit_type, tenant_id } = params;

    const filteredDefects = useMemo(() => options?.filter((i) => filter.position.includes(i.position) && filter.severity.includes(i.severity)) ?? [], [options, filter, fruit_type]);

    // * refresh the tenant object if needed
    useEffect(() => {
        if (tenant_id) {
            dispatch(getTenant(tenant_id) as AvosAction);
        }
    }, [tenant_id]);

    // * fetch the fruit_type options that we are currently editing
    useEffect(() => {
        if (tenant_id && fruit_type) {
            dispatch(getTenantDefects(tenant_id, fruit_type) as AvosAction);
        }
    }, [fruit_type, tenant_id]);

    // * if the fruit_type changes, navigate to the new fruit_type
    const first_fruit_type = useMemo(() => config?.fruit_types?.[0]?.value, [config?.fruit_types]);

    useEffect(() => {
        if (filter.fruit_type !== fruit_type && filter.fruit_type) {
            navigate(`/tenants/${tenant_id}/defects/${filter.fruit_type}`);
        } else if (!fruit_type && first_fruit_type) {
            // * if no fruit_type is set, filter for the first fruit_type
            setFilter((prev) => ({ ...prev, fruit_type: first_fruit_type }));
        }
    }, [filter.fruit_type, fruit_type, first_fruit_type, tenant_id, navigate]);

    // Update filter when defect positions or severities change from config
    useEffect(() => {
        setFilter((prev) => ({
            ...prev,
            position: defectPositions,
            severity: defectSeverities
        }));
    }, [defectPositions, defectSeverities]);

    const fileChangeHandler = (e) => {
        const file = e.target.files[0];

        if (file) {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete(results) {
                    const options = results.data.map((i) => ({
                        ...defaultCsvOption,
                        position: (i.position?.trim() && defectPositions.includes(i.position.trim())) ? i.position.trim() : null,
                        severity: (i.severity?.trim() && defectSeverities.includes(i.severity.trim())) ? i.severity.trim() : null,
                        fruit_type: i.fruit_type ? i.fruit_type.trim().toLowerCase() : fruit_type,
                        label: i.label ? i.label.trim() : null,
                        description: i.description ? i.description.trim() : null,
                        value: i.value || (i.label?.trim() ? i.label.trim().replaceAll(" ", "-").toLowerCase() : null),
                        blob_name: i.blob_name ? i.blob_name : null
                    })).map((i) => ({
                        ...i,
                        defect_id: `${i.position}_${i.severity}_${i.value}`
                    }));

                    setImportData(options);
                    setSelectedFile(file);
                },
            });
        }
    };

    const handleImport = () => dispatch(importTenantDefect(params.tenant_id, fruit_type, filteredImportData) as AvosAction);

    const fruitOptions = useMemo(() => config?.fruit_types?.map((i) => ({ value: i.value, label: i.text })), [config?.fruit_types]);

    // Determine safe defaults that exist in the configured values
    const defaultPosition = useMemo(() => {
        if (defectPositions.includes("internal")) return "internal";
        return defectPositions[0] || "";
    }, [defectPositions]);

    const defaultSeverity = useMemo(() => {
        if (defectSeverities.includes("minor")) return "minor";
        return defectSeverities[0] || "";
    }, [defectSeverities]);

    const form = useMemo(() => [
        {
            label: "Position / Field",
            name: "position",
            type: "radio",
            default: defaultPosition,
            options: defectPositionOptions,
            mandatory: true,
        },
        {
            label: "Severity / Category",
            name: "severity",
            type: "radio",
            default: defaultSeverity,
            options: defectSeverityOptions,
            mandatory: true,
        },
        {
            label: "Label",
            name: "label",
            type: "text",
            mandatory: true,
        },
        {
            label: "Description",
            name: "description",
            type: "text",
        },
        {
            label: "Value",
            name: "value",
            type: "text",
            mandatory: true,
            description: "The value of the defect. E.g. `sunburn` or `vascular-browning`",
            onchange_callback: ({ setValue, field, value }) => {
                setValue(field, value.replaceAll(" ", "-").replaceAll("_", "-").toLowerCase());
            },
        },
        {
            label: "Defect image",
            name: "blob_name",
            description: "Upload defect image. Webp format only. https://cloudinary.com/tools/image-to-webp",
            type: "file-select",
            accept: ["image/webp"],
            upload_config_image: true
        }
    ], [defectPositionOptions, defectSeverityOptions, defaultPosition, defaultSeverity]);

    const deleteOption = (option) => dispatch(deleteTenantDefect(tenant_id, option.fruit_type, option.id) as AvosAction);

    const deleteAll = () => dispatch(deleteAllTenantDefect(tenant_id, fruit_type) as AvosAction);

    const filterForm = useMemo(() => [
        {
            label: "Fruit type",
            name: "fruit_type",
            type: "single-select",
            options: fruitOptions,
            lg: 4,
        },
        {
            label: "Position / Field",
            name: "position",
            type: "single-select",
            return_array: true,
            multi: true,
            lg: 4,
            options: defectPositionOptions,
        },
        {
            label: "Severity / Category",
            name: "severity",
            type: "single-select",
            return_array: true,
            multi: true,
            lg: 4,
            options: defectSeverityOptions,
        }
    ], [fruitOptions, defectPositionOptions, defectSeverityOptions]);

    const onCancel = () => {
        setModal(false);
        setQuery(defaultValues);
    };

    const onAddNewItem = () => {
        setModal(true);
        setQuery(defaultValues);
    };

    const onSubmit = () => {
        if (!metaFormIsValid(form, query, true)) {
            return;
        }
        if (query.id) {
            dispatch(updateTenantDefect(tenant_id, fruit_type, query) as AvosAction);
        } else {
            dispatch(createTenantDefect(tenant_id, fruit_type, { ...query, fruit_type }) as AvosAction);
        }

        setModal(true);
        setQuery(defaultValues);
    };

    const exportData = (data, fileName, type) => {
        // Create a link and download the file
        const blob = new Blob([data], { type });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const exportOptions = () => {
        const csvData = Papa.unparse(options);
        exportData(csvData, `export-${tenant_name}-${formatDateForFilename(edFormats.datetime_format)}-${params.fruit_type}-options.csv`, "text/csv;charset=utf-8;");
    };

    const filteredImportData = importData.filter((i) => i.label && i.value);

    const buttons = (
        <>
            <Button className="text-nowrap" color="primary" onClick={onAddNewItem} icon={faPlus}>
                Add option
            </Button>

            <Button className="text-nowrap" color="primary" onClick={() => setImportModal(true)} icon={faExchange}>
                Import
            </Button>
        </>
    );

    if (!options && isLoading) return <Loading></Loading>;
    return (
        <FullPageLayout>
            <FormContainer
                title="Labels"
                subtitle={<span>Edit tenant <b>{tenant_name}</b> labels for <b>{fruit_type}</b></span>}
                actionsTop={buttons}
                backTo={`/tenants/${tenant_id}`}
                fullWidth
            >
                <MetaForm
                    meta={filterForm}
                    grid={true} object={filter}
                    setValue={(field, value) => setFilter((oldFilter) => ({ ...oldFilter, [field]: value }))}
                    config={config}
                />

                <Table size="sm">
                    <thead>
                        <tr>
                            <th>pk</th>
                            <th>defect_id</th>
                            <th>Position / Field</th>
                            <th>Severity / Category</th>
                            <th>Value</th>
                            <th>Label</th>
                            <th>Description</th>
                            <th>Defect image</th>
                            <th />
                        </tr>
                    </thead>

                    <tbody>
                        {(filteredDefects || []).map((i, index) => (
                            <tr key={index} >
                                <td className="align-middle">{i.id.slice(0, 7)}</td>
                                <td className="align-middle">{i.defect_id}</td>
                                <td className="align-middle">{i.position}</td>
                                <td className="align-middle">{i.severity}</td>
                                <td className="align-middle">{i.value}</td>
                                <td className="align-middle">{i.label}</td>
                                <td className="align-middle">{i.description}</td>
                                <td className="align-middle">{i?.image_url ? (
                                    <img
                                        src={i?.image_url}
                                        alt="Selected image"
                                        className="file-preview-image ms-3"
                                        css={css`object-fit: contain;`}
                                        width="80px"
                                        height="80px"
                                    />
                                ) : "No image"}
                                </td>

                                <td className="text-end">
                                    <Button
                                        color="primary"
                                        className="me-1 mb-1"
                                        outline
                                        onClick={() => {
                                            setQuery(i);
                                            setModal(true);
                                        }}
                                    >
                                        Edit
                                    </Button>

                                    <Button color="danger" className="me-1 mb-1" outline onClick={() => deleteOption(i)}>
                                        Delete
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </FormContainer>

            <Modal size="md" isOpen={isOpen} toggle={onCancel} >
                <ModalHeader toggle={onCancel}>
                    {query?.id ? "Edit" : "New"} {fruit_type} defect
                </ModalHeader>

                <ModalBody>
                    <MetaForm
                        meta={form}
                        setValue={(field, value) => setQuery((query) => ({ ...query, [field]: value }))}
                        object={query}
                        config={config}
                    />
                </ModalBody>

                <ModalFooter>
                    <Button color="light" onClick={onCancel}>
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        disabled={!query.label || !query.value || isLoading}
                        className="me-1 mb-1"
                        onClick={onSubmit}
                    >
                        Submit
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal size="xl" isOpen={importModal} toggle={() => setImportModal(false)}>
                <ModalHeader toggle={() => setImportModal(false)}>
                    Import / Delete all
                </ModalHeader>

                <ModalBody>
                    <FormGroup>
                        <Label for="exampleFile">
                            CSV
                        </Label>

                        <Input
                            id="exampleFile"
                            name="file"
                            type="file"
                            onChange={fileChangeHandler}
                        />

                        {selectedFile ? (
                            <div className="py-3">
                                <FormText>
                                    <div>Filename: {selectedFile.name}</div>
                                    <div>Filetype: {selectedFile.type}</div>
                                    <div>Size in bytes: {selectedFile.size}</div>
                                    <div>Items: {importData && importData.length}</div>
                                    <div>Valid items: {importData && filteredImportData.length}</div>
                                </FormText>
                            </div>
                        ) : (
                            <FormText>
                                Upload csv file without headers here.
                            </FormText>
                        )}
                    </FormGroup>

                    {importData.length > 0 && (
                        <Table size="sm" responsive>
                            <thead>
                                <tr>
                                    <th>Fruit type</th>
                                    <th>defect_id</th>
                                    <th>Position / Field</th>
                                    <th>Severity / Category</th>
                                    <th>Value</th>
                                    <th>Label</th>
                                    <th>Description</th>
                                    <th>Blob name</th>
                                </tr>
                            </thead>

                            <tbody>
                                {importData.slice(0, showAllToBeImportedItems ? importData.length : 10).map((i, index) => (
                                    <tr key={index} >
                                        <td className={`align-middle ${i.fruit_type || "bg-danger"}`}>{i.fruit_type}</td>
                                        <td className={`align-middle ${i.defect_id || "bg-danger"}`}>{i.defect_id}</td>
                                        <td className={`align-middle ${i.position || "bg-danger"}`}>{i.position}</td>
                                        <td className={`align-middle ${i.severity || "bg-danger"}`}>{i.severity}</td>
                                        <td className={`align-middle ${i.value || "bg-danger"}`}>{i.value}</td>
                                        <td className={`align-middle ${i.label || "bg-danger"}`}>{i.label}</td>
                                        <td className={`align-middle ${i.description || "bg-danger"}`}>{i.description}</td>
                                        <td className={`align-middle ${i.blob_name || "bg-danger"}`}>{i.blob_name}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}

                    {importData.length > 10 && !showAllToBeImportedItems && (
                        <div className="text-center py-3">
                            <span onClick={() => setShowAllToBeImportedItems(true)} className="btn-link">
                                Show all
                            </span>
                        </div>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button color="light" onClick={() => setImportModal(false)}>
                        Cancel
                    </Button>

                    <Button color="light" className="me-1 mb-1" onClick={exportOptions}>
                        Export
                    </Button>

                    <Button
                        color="primary"
                        disabled={isLoading || !importData || filteredImportData.length === 0}
                        className="me-1 mb-1"
                        onClick={handleImport}
                    >
                        Import
                    </Button>

                    <Button
                        color="danger"
                        disabled={isLoading}
                        className="me-1 mb-1"
                        onClick={deleteAll}
                    >
                        Delete All
                    </Button>
                </ModalFooter>
            </Modal>
        </FullPageLayout>
    );
}
